import { Carrier } from 'store/api/carriers';
import { User, Company, IdObject } from 'types';
import { makeSelectOptionsFromOptionsArray } from 'utils/helpers';

import { Product } from '../product/types';

export enum StockTransferType {
  EventToStorage = 'EventToStorage',
  StorageToStorage = 'StorageToStorage',
  StorageToEvent = 'StorageToEvent',
  EventToEvent = 'EventToEvent',
  ClientToClient = 'ClientToClient',
}

export enum StockTransferStatus {
  Open = 'Open',
  Paused = 'Paused',
  InTransit = 'InTransit',
  Completed = 'Completed',
}

export interface StockTransfer extends IdObject {
  date: string;
  createdBy: User;
  companyId: number;
  carrier?: Carrier;
  // LTL freight on the transfer. Kept distinct from `wholesaleCost`: the goods
  // being moved are already owned, so only the freight is a new cost.
  ltlPrice?: string;
  carrierId?: number;
  wholesaleCost: number;
  type: StockTransferType;
  sourceStorage?: Storage;
  // Owner 2026-10-16: the carrier's work order, as on a stock order (Russ
  // invoices say "Reference WO 9244").
  workOrder?: string | null;
  status: StockTransferStatus;
  destinationStorage?: Storage;
  destinationCompany?: Company;
  // Russell Logistics invoice # for the LTL load (Invoice # = Load #).
  ltlInvoiceNumber?: string | null;
  // answers the "LTL Invoice # already on another record" check (409)
  confirmSharedLtlInvoice?: boolean;
  // T-L09 (owner rule L14) / owner 2026-10-16: the events the LTL freight is
  // for, each with its share (adds up to ltlPrice exactly); every type but
  // Client -> Client; saved rows win over the receiving event.
  eventAllocations?: StockTransferEventAllocation[];
  items: {
    cases: number;
    count: number;
    price: number;
    units: number;
    maxCount: number;
    product: Product;
    productId: number;
  }[];
}

// T-L09: one event's share of a Storage -> Storage transfer's LTL freight
// (StockTransferEventAllocations; DECIMAL values come back as strings).
export interface StockTransferEventAllocation {
  id?: number;
  amount: string;
  eventId: number;
  percent: string;
  event?: { id: number; name: string };
}

export type StockTransferEventAllocationRequest = Pick<
  StockTransferEventAllocation,
  'amount' | 'eventId' | 'percent'
>;

export const stockTransferTypeLabel: Record<StockTransferType, string> = {
  [StockTransferType.EventToEvent]: 'Event - Event',
  [StockTransferType.StorageToEvent]: 'Storage - Event',
  [StockTransferType.EventToStorage]: 'Event - Storage',
  [StockTransferType.ClientToClient]: 'Client - Client',
  [StockTransferType.StorageToStorage]: 'Storage - Storage',
};

export const stockTransferTypeOptions = makeSelectOptionsFromOptionsArray(
  Object.values(StockTransferType),
  stockTransferTypeLabel
);

export const stockTransferStatusLabel: Record<StockTransferStatus, string> = {
  [StockTransferStatus.Open]: 'Open',
  [StockTransferStatus.Paused]: 'Paused',
  [StockTransferStatus.Completed]: 'Completed',
  [StockTransferStatus.InTransit]: 'In transit',
};

export const stockTransferStatusOptions = makeSelectOptionsFromOptionsArray(
  Object.values(StockTransferStatus),
  stockTransferStatusLabel
);

export interface StockTransferFormValues extends Omit<
  StockTransfer,
  'sourceStorage' | 'destinationStorage'
> {
  sourceStorageId: number | null;
  sourceCompanyId?: number | null;
  destinationStorageId: number | null;
  destinationCompanyId?: number | null;
  companyTransferType?: 'event' | 'storage';
}

export interface StockTrnasferAcceptRequest
  extends
    Pick<StockTransferFormValues, 'destinationStorageId'>,
    Pick<StockTransfer, 'id'> {
  eventId?: number;
}

export type StockTransferListType = 'other' | 'company';
